import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLanguage } from '../../contexts/LanguageContext';
import type { GridPaginationModel } from '@mui/x-data-grid';

interface CustomTablePaginationProps {
  paginationModel: GridPaginationModel;
  rowCount: number;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  pageSizeOptions?: number[];
}

export default function CustomTablePagination({
  paginationModel,
  rowCount,
  onPaginationModelChange,
  pageSizeOptions = [25, 50, 75, 100],
}: CustomTablePaginationProps) {
  const { t, direction } = useLanguage();
  const isRtl = direction === 'rtl';

  const { page, pageSize } = paginationModel;
  const totalPages = Math.ceil(rowCount / pageSize);
  const from = rowCount === 0 ? 0 : page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, rowCount);

  const handlePreviousPage = () => {
    if (page > 0) {
      onPaginationModelChange({ ...paginationModel, page: page - 1 });
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      onPaginationModelChange({ ...paginationModel, page: page + 1 });
    }
  };

  const handlePageSizeChange = (event: any) => {
    onPaginationModelChange({
      page: 0,
      pageSize: parseInt(event.target.value, 10),
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 16px',
        borderTop: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      {/* Rows per page selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t('dataGrid.MuiTablePagination.labelRowsPerPage')}
        </Typography>
        <FormControl size="small" variant="outlined">
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            sx={{
              minWidth: 70,
              '& .MuiSelect-select': {
                py: 0.5,
                fontSize: '0.875rem',
              },
            }}
          >
            {pageSizeOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Page info and navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Page info */}
        <Typography variant="body2" color="text.secondary">
          {t('dataGrid.MuiTablePagination.labelDisplayedRows', {
            from: from.toString(),
            to: to.toString(),
            count: rowCount.toString(),
          })}
        </Typography>

        {/* Navigation buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isRtl ? (
            <>
              {/* Next button (on left in RTL) */}
              <Button
                size="small"
                variant="outlined"
                onClick={handleNextPage}
                disabled={page >= totalPages - 1}
                endIcon={<ChevronLeftIcon />}
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  textTransform: 'none',
                }}
              >
                {t('dataGrid.next')}
              </Button>

              {/* Page indicator */}
              <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                {t('dataGrid.page')} {page + 1} {t('dataGrid.of')} {totalPages}
              </Typography>

              {/* Previous button (on right in RTL) */}
              <Button
                size="small"
                variant="outlined"
                onClick={handlePreviousPage}
                disabled={page === 0}
                startIcon={<ChevronRightIcon />}
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  textTransform: 'none',
                }}
              >
                {t('dataGrid.previous')}
              </Button>
            </>
          ) : (
            <>
              {/* Previous button (on left in LTR) */}
              <Button
                size="small"
                variant="outlined"
                onClick={handlePreviousPage}
                disabled={page === 0}
                startIcon={<ChevronLeftIcon />}
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  textTransform: 'none',
                }}
              >
                {t('dataGrid.previous')}
              </Button>

              {/* Page indicator */}
              <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                {t('dataGrid.page')} {page + 1} {t('dataGrid.of')} {totalPages}
              </Typography>

              {/* Next button (on right in LTR) */}
              <Button
                size="small"
                variant="outlined"
                onClick={handleNextPage}
                disabled={page >= totalPages - 1}
                endIcon={<ChevronRightIcon />}
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  textTransform: 'none',
                }}
              >
                {t('dataGrid.next')}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

